'use client'

import Image from 'next/image'
import { motion, useReducedMotion } from 'motion/react'
import { getAssetKey, getThumbnail } from '@/lib/assets/assets'
import type { AssetSummary } from '@/lib/assets/types'
import styles from './preview-player-bar.module.scss'

export type PreviewJump = {
  id: number
  origin: number
  destination: number
  duration: number
  phase: 'holding' | 'travelling'
  targetKey?: string
}

export function PreviewPlayerFilmstrip({
  assets,
  jump,
  range,
}: {
  assets: AssetSummary[]
  jump: PreviewJump
  range?: { start: number; end: number }
}) {
  const reduceMotion = useReducedMotion()
  const resolvedTarget = jump.targetKey ? assets.findIndex((asset) => getAssetKey(asset) === jump.targetKey) : -1
  const active =
    jump.phase === 'holding'
      ? jump.origin
      : resolvedTarget >= 0
        ? resolvedTarget
        : Math.min(jump.destination, assets.length - 1)
  const start = range?.start ?? Math.max(0, Math.min(jump.origin, jump.destination) - 4)
  const end = range?.end ?? Math.min(assets.length, Math.max(jump.origin, jump.destination) + 5)

  return (
    <motion.div
      className={styles.filmstrip}
      data-slot="preview-player-filmstrip"
      data-phase={jump.phase}
      aria-hidden="true"
      initial={{ opacity: 0, y: reduceMotion ? 0 : 32 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: reduceMotion ? 0 : 12 }}
      transition={{ duration: reduceMotion ? 0 : 0.18, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className={styles.filmstripWindow}>
        <motion.div
          className={styles.filmstripTrack}
          initial={false}
          animate={{ x: -(active - start) * 80 - 36 }}
          transition={{ duration: reduceMotion ? 0 : jump.duration, ease: [0.22, 1, 0.36, 1] }}
        >
          {assets.slice(start, end).map((asset, offset) => {
            const thumbnail = getThumbnail(asset)
            const distance = Math.abs(start + offset - active)
            return (
              <motion.div
                key={getAssetKey(asset)}
                className={styles.filmstripThumbnail}
                data-selected={distance === 0}
                animate={{
                  opacity: distance === 0 ? 1 : Math.max(0.25, 0.65 - distance * 0.08),
                  scale: distance === 0 ? 1 : 0.9,
                }}
                transition={{ duration: reduceMotion ? 0 : 0.3 }}
              >
                {/* The bounded strip reuses image previews without allocating video players. */}
                {thumbnail && (
                  <Image src={thumbnail} alt="" width={72} height={48} unoptimized loading="eager" draggable={false} />
                )}
              </motion.div>
            )
          })}
        </motion.div>
        <span className={styles.filmstripCursor} />
      </div>
    </motion.div>
  )
}
